import { useDispatch } from "react-redux";
import { AppDispatch, IMainContext, SubModule } from "@ts/common-types";
import { Button, Popover, Select, Space } from "antd";
import { useContext, useMemo, useState } from "react";
import GoalStatus from "./GoalStatus";
import GoalsGrid from "./GoalsGrid";
import {
    setGoalListStatusType,
    setGoalModalTitle,
    setIsGoalFormSubmitted,
    updateAddGoalsModalDisplay,
} from "@/features/modalSlice";
import { agBtnExport, populateGoals, refreshGoalsResult } from "@/ts/utils";
import { mainContext } from "@/ts/constants";
import { setGoalData } from "@/features/gridDataSlice";
import { SyncOutlined } from "@ant-design/icons";
import { showToast } from "@/ts/modals";
import { GridApi } from "ag-grid-community";
import ExportGridBtn from "@/components/buttons/ExportGridBtn";
import { usePermissionCheck } from "@/hooks/AllCustomHooks";

const { Option } = Select;
const Goals = () => {
    const [refreshLoading, setRefreshLoading] = useState<boolean>(false);
    const { setGoalsGridLoading, cerbosPermissions } =
        useContext<IMainContext>(mainContext);
    const [goalsGridApi, setGoalsGridApi] = useState<GridApi | null>(null);
    const dispatch = useDispatch<AppDispatch>();

    const handleGoalStatusOptionChange = async (value: string) => {
        dispatch(setGoalListStatusType(value));

        dispatch(setGoalData([]));
        setGoalsGridLoading(true);

        const goalsData = await populateGoals(value);

        dispatch(setGoalData(goalsData));
        setGoalsGridLoading(false);
    };

    const handleGoalsRefresh = async () => {
        setRefreshLoading(true);

        const refreshResponse = await refreshGoalsResult();

        if (refreshResponse.status) {
            dispatch(setGoalData([]));
            setGoalsGridLoading(true);

            const goalsData = await populateGoals();

            dispatch(setGoalData(goalsData));
            setGoalsGridLoading(false);

            showToast("success", refreshResponse.message);
        } else {
            showToast("danger", refreshResponse.message);
        }

        setRefreshLoading(false);
    };

    const { canCreateGoal } = usePermissionCheck();

    return (
        <>
            <div
                className="card row h-100 shadow"
                style={{
                    borderRadius: "16px",
                    margin: "0 auto",
                    maxWidth: "100%",
                }}
            >
                <div className="card-body p-2" style={{ borderRadius: "16px" }}>
                    <div className="d-flex align-items-center justify-content-between px-2 pt-1 flex-wrap">
                        <h5 className="flex-grow-1 mb-md-0">Goals</h5>
                        <Space>
                            <Select
                                onChange={handleGoalStatusOptionChange}
                                defaultValue={"active"}
                                style={{
                                    marginTop: 3,
                                    width: 100,
                                }}
                            >
                                <Option value={"active"}>Active</Option>
                                <Option value={"inactive"}>Archived</Option>
                                <Option value={"all"}>All</Option>
                            </Select>
                            <Popover
                                content={
                                    <div className="fw-bold">
                                        Goal results are being refreshed...
                                    </div>
                                }
                                styles={{
                                    body: {
                                        padding: "15px",
                                        width: "250px",
                                        textAlign: "center",
                                    },
                                }}
                                trigger={refreshLoading ? "hover" : "click"}
                                // open={refreshLoading}
                            >
                                <Button
                                    style={{
                                        height: "33px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        padding: 0,
                                        marginTop: 2,
                                    }}
                                    icon={<SyncOutlined />}
                                    loading={refreshLoading}
                                    onClick={handleGoalsRefresh}
                                />
                            </Popover>
                            <Button
                                id="add-goals-btn"
                                type="primary"
                                onClick={async () => {
                                    dispatch(setIsGoalFormSubmitted(false));
                                    dispatch(updateAddGoalsModalDisplay(true));
                                    dispatch(setGoalModalTitle("Add"));
                                }}
                                disabled={!canCreateGoal}
                            >
                                Add Goal
                            </Button>
                            <ExportGridBtn
                                onClickFunction={() =>
                                    agBtnExport(goalsGridApi, true, "Goals")
                                }
                                isSubModule={true}
                            />
                        </Space>
                    </div>
                    <div className="border-top pt-2 mt-2" />
                </div>
                <div className="container">
                    <div className="row">
                        <div className="col-12 col-sm-12 col-md-3">
                            <div className="d-flex p-3 align-items-center flex-column">
                                <GoalStatus />
                            </div>
                        </div>
                        <div className="col-12 col-sm-12 col-md-9">
                            <GoalsGrid
                                gridApi={goalsGridApi}
                                setGridApi={setGoalsGridApi}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Goals;
